import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AIModel, GlobalSettings, ModelsResponse, User } from '@volt/shared';
import type { ApiClient } from '../api/client.js';
import { ApiError, createApiClient } from '../api/client.js';
import { REFRESH_TOKEN_KEY, secureGet, secureRemove, secureSet } from '../lib/storage.js';
import { getInitData, hasTelegram } from '../telegram/webapp.js';

export type SessionStatus =
  | 'booting'
  | 'authenticated'
  | 'no-telegram'
  | 'error';

export interface SessionData {
  user: User;
  balanceKopecks: number;
  settings: GlobalSettings;
  models: AIModel[];
  modelSettings: ModelsResponse['settings'];
}

export interface SessionValue {
  api: ApiClient;
  status: SessionStatus;
  data: SessionData | null;
  error: string | null;
  isAdmin: boolean;
  retry: () => void;
  setBalance: (balanceKopecks: number) => void;
  patchUser: (patch: Partial<User>) => void;
  refreshModels: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message || 'error.generic';
  if (error instanceof Error) return error.message;
  return 'error.generic';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const api = useMemo(() => createApiClient(), []);
  const [status, setStatus] = useState<SessionStatus>('booting');
  const [data, setData] = useState<SessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const booted = useRef(false);

  const loadSession = useCallback(async () => {
    const [me, models] = await Promise.all([api.getMe(), api.getModels()]);
    setData({
      user: me.user,
      balanceKopecks: me.balanceKopecks,
      settings: me.settings,
      models: models.models,
      modelSettings: models.settings,
    });
    setStatus('authenticated');
    setError(null);
  }, [api]);

  const boot = useCallback(async () => {
    setStatus('booting');
    setError(null);
    try {
      const stored = await secureGet(REFRESH_TOKEN_KEY);
      if (stored) {
        api.setTokens({ accessToken: '', refreshToken: stored });
        if (await api.refresh()) {
          await loadSession();
          return;
        }
      }
      if (hasTelegram()) {
        const auth = await api.authTelegram(getInitData());
        api.setTokens({ accessToken: auth.accessToken, refreshToken: auth.refreshToken });
        await secureSet(REFRESH_TOKEN_KEY, auth.refreshToken);
        await loadSession();
        return;
      }
      setStatus('no-telegram');
    } catch (err) {
      console.error('[session] boot failed', err instanceof ApiError
        ? { status: err.status, code: err.code, message: err.message }
        : err);
      setError(errorText(err));
      setStatus('error');
    }
  }, [api, loadSession]);

  useEffect(() => {
    const off = api.onTokens((tokens) => {
      if (tokens?.refreshToken) {
        void secureSet(REFRESH_TOKEN_KEY, tokens.refreshToken);
      } else {
        void secureRemove(REFRESH_TOKEN_KEY);
      }
    });
    api.setAuthUpdateHandler((auth) => {
      setData((prev) =>
        prev ? { ...prev, user: auth.user, balanceKopecks: auth.balanceKopecks } : prev,
      );
    });
    return () => {
      off();
      api.setAuthUpdateHandler(null);
    };
  }, [api]);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    void boot();
  }, [boot]);

  const setBalance = useCallback((balanceKopecks: number) => {
    setData((prev) => (prev ? { ...prev, balanceKopecks } : prev));
  }, []);

  const patchUser = useCallback((patch: Partial<User>) => {
    setData((prev) => (prev ? { ...prev, user: { ...prev.user, ...patch } } : prev));
  }, []);

  const refreshModels = useCallback(async () => {
    // Do not refresh if not yet authenticated — avoids unauthenticated requests
    // during the boot sequence that waste rate-limit budget.
    if (status !== 'authenticated') return;
    const models = await api.getModels();
    setData((prev) =>
      prev ? { ...prev, models: models.models, modelSettings: models.settings } : prev,
    );
  }, [api, status]);

  const refreshMe = useCallback(async () => {
    const me = await api.getMe();
    setData((prev) =>
      prev
        ? { ...prev, user: me.user, balanceKopecks: me.balanceKopecks, settings: me.settings }
        : prev,
    );
  }, [api]);

  const value = useMemo<SessionValue>(
    () => ({
      api,
      status,
      data,
      error,
      isAdmin: data?.user.isAdmin ?? false,
      retry: () => void boot(),
      setBalance,
      patchUser,
      refreshModels,
      refreshMe,
    }),
    [api, status, data, error, boot, setBalance, patchUser, refreshModels, refreshMe],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}

/** Access session data inside authenticated screens where it is guaranteed set. */
export function useSessionData(): SessionValue & { data: SessionData } {
  const ctx = useSession();
  if (!ctx.data) throw new Error('Session data is not available yet');
  return ctx as SessionValue & { data: SessionData };
}
